'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const demoCredentials = [
  { role: 'State Coordinator', email: 'state.coordinator@prakritiveeer.bk.org', password: 'NatureFirst@2026' },
  { role: 'Centre Coordinator', email: 'centre.pune@prakritiveeer.bk.org', password: 'PuneGreen@2026' },
];

interface AuthScreenProps {
  onCredentialSelect?: (email: string, password: string) => void;
}

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Left panel — nature brand */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a3c2b 0%, #2d6a4f 45%, #52b788 100%)' }}>
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #e9c46a, transparent)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #52b788, transparent)', transform: 'translate(-30%, 30%)' }} />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={44} />
            <div>
              <span className="font-extrabold text-xl text-white leading-tight block">PrakritiVeer</span>
              <span className="text-xs text-white/70 font-medium">A Brahma Kumaris Initiative</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <h2 className="text-3xl font-extrabold text-white leading-tight text-balance">
              Respecting<br />Mother Nature
            </h2>
            <p className="text-white/70 text-sm mt-3 leading-relaxed max-w-xs">
              Log your centre's environmental activities, track progress, and inspire others to become Environmental Champions.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { icon: 'GlobeAltIcon', label: 'Kalp Taruh — 1 Person, 1 Plant, 1 Planet' },
              { icon: 'TrophyIcon', label: 'Identify Prakriti Veer Champions' },
              { icon: 'SparklesIcon', label: 'Sakash — Healing the 5 Elements' },
              { icon: 'HomeModernIcon', label: 'Gokul Gram Village Transformation' },
            ].map((item) => (
              <div key={`brand-${item.label}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Icon name={item.icon as any} size={16} variant="outline" className="text-white" />
                </div>
                <span className="text-sm text-white/80 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/40 text-xs">
            © 2026 Brahma Kumaris — Rajat Rashmiyan Campaign
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 lg:px-12 xl:px-16 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <div>
            <span className="font-extrabold text-lg text-primary">PrakritiVeer</span>
            <span className="block text-xs text-muted-foreground">Respecting Mother Nature</span>
          </div>
        </div>

        <div className="w-full max-w-md">
          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-muted p-1 rounded-xl mb-7">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${mode === 'login' ? 'tab-active' : 'tab-inactive'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${mode === 'signup' ? 'tab-active' : 'tab-inactive'}`}
            >
              Register
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm demoCredentials={demoCredentials} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}